import { AllessehContent } from 'hooks/useAllessehContentQuery';

export const defaultSummaryHeadline = (articleContent: AllessehContent) =>
  articleContent.data.attributes.summary?.content
    ?.find((paragraphObject) => paragraphObject.type === 'paragraph' && Array.isArray(paragraphObject.content))
    ?.content?.find((textObject) => textObject.type === 'headline')?.content?.[0]?.text ??
  articleContent.data.attributes.headline?.text;

export const agencyFromCredit = (credit: string | undefined): string | undefined => {
  if (!credit) return undefined;
  // Return the full credit string so all agencies can be matched (e.g., "DADO RUVIC/REUTERS" checks both parts)
  return credit.trim();
};

export const defaultImageAgency = (
  articleContent: AllessehContent,
  defaultAltSumm?: string | null
): string | undefined => {
  // Validate alt_summaries exists
  const altSummaries = articleContent.data.attributes.alt_summaries;
  if (!altSummaries || !Array.isArray(altSummaries)) {
    return undefined;
  }

  // Non-Images variants are stored with pipe delimiters (e.g. "|APIImage|"), matching getAltSummForVariant convention
  const variantToMatch = defaultAltSumm && defaultAltSumm !== 'Images' ? `|${defaultAltSumm}|` : defaultAltSumm;
  // Prefer the publication's default alt-summ variant (e.g. APIImage for newswires), fall back to Images
  const imagesSummary =
    (variantToMatch ? altSummaries.find((altSummary) => altSummary.variant === variantToMatch) : undefined) ??
    altSummaries.find((altSummary) => altSummary.variant === 'Images');
  if (!imagesSummary) {
    return undefined;
  }

  // Validate content exists and get the ref
  const { content } = imagesSummary;
  if (!content || !Array.isArray(content) || content.length === 0) {
    return undefined;
  }

  const imageRef = content[0].ref;
  if (!imageRef) {
    return undefined;
  }

  // Validate links.related exists
  const relatedLinks = articleContent.links?.related;
  if (!relatedLinks || !Array.isArray(relatedLinks)) {
    return undefined;
  }

  // Find the matching image
  const image = relatedLinks.find((relatedLink) => relatedLink.type === 'image' && relatedLink.id === imageRef);

  if (!image) {
    return undefined;
  }

  // Get credit with fallback
  const { credit } = image;

  return agencyFromCredit(credit);
};

export const defaultImage = (articleContent: AllessehContent) => {
  const relatedLinks = articleContent.links?.related;
  const imageLink = relatedLinks?.find((relatedLink) => relatedLink.type === 'image');
  const altSummariesImageLink = relatedLinks?.find(
    (relatedLink) =>
      relatedLink.type === 'image' &&
      relatedLink.id ===
        articleContent.data.attributes.alt_summaries?.find((altSummary) => altSummary.variant === 'Images')
          ?.content?.[0]?.ref
  );

  if (altSummariesImageLink) {
    return altSummariesImageLink.properties?.location ?? altSummariesImageLink.name;
  }

  return imageLink?.properties?.location ?? imageLink?.name;
};
